import React from 'react';
import { IoClose } from 'react-icons/io5';

import { Tooltip } from 'antd';
import NumericInputField from 'components/common/NumericInputField';
import {
  CLEAR_BUTTON_LABEL,
  TASKS_GRID_APPLICATION_DETAILS_HEADING,
  TASKS_GRID_NOTES_FIELD_LABEL,
  TASKS_GRID_TARGET_FIELD_LABEL,
  TASKS_GRID_WATER_RATE_FIELD_LABEL,
  TASKS_GRID_WATER_RATE_PLACEHOLDER,
  TASKS_GRID_WATER_RATE_UNIT_LABEL,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInputTask, IInputTaskBase } from 'interfaces/IInputTask';

import FieldTogglePill from './FieldTogglePill';
import { getTaskDetailFieldState, TaskDetailField } from './taskDetailFields';
import { ApplicationTaskFields } from './taskFactory';
import TargetSpeciesPicker from './TargetSpeciesPicker';

interface ClearFieldButtonProps {
  label: string;
  onClear: () => void;
}

// Clearing a value is its own action, separate from hiding the field.
export const ClearFieldButton: React.FC<ClearFieldButtonProps> = ({
  label,
  onClear,
}) => {
  const name = `${CLEAR_BUTTON_LABEL} ${label.toLowerCase()}`;
  return (
    <Tooltip title={name}>
      <button
        type="button"
        aria-label={name}
        className="flex-shrink-0 rounded p-1 text-gray-dark hover:bg-rowHover"
        onClick={onClear}
      >
        <IoClose size={14} aria-hidden="true" />
      </button>
    </Tooltip>
  );
};

interface TaskInlineDetailsProps {
  task: IInputTask;
  gUnits: GUnit[];
  fieldVisibilityOverride: Map<string, boolean>;
  setFieldVisible: (taskId: string, field: string, visible: boolean) => void;
  commitTaskField: (
    task: IInputTask,
    patch: Partial<IInputTaskBase & ApplicationTaskFields>
  ) => void;
}

// An expanded task's Application Details buttons, on the task row beside its
// name (OP-2534): Water rate and Target open their fields in place, right
// after their buttons, and Notes opens on its own line below the row (see
// TaskDetailsSection). Wraps under the task name when the column is narrow.
const TaskInlineDetails: React.FC<TaskInlineDetailsProps> = ({
  task,
  gUnits,
  fieldVisibilityOverride,
  setFieldVisible,
  commitTaskField,
}) => {
  const {
    applicationTask,
    hasWaterRate,
    hasTarget,
    hasNotes,
    showWaterRate,
    showTarget,
    showNotes,
  } = getTaskDetailFieldState(task, gUnits, fieldVisibilityOverride);
  const toggle = (field: TaskDetailField, visible: boolean) =>
    setFieldVisible(task.taskId, field, visible);

  return (
    // The row toggles expand on click; working in these controls shouldn't.
    <div
      role="group"
      aria-label={TASKS_GRID_APPLICATION_DETAILS_HEADING}
      className="flex flex-wrap items-center gap-x-3 gap-y-1 font-normal"
      onClick={(event) => event.stopPropagation()}
    >
      {applicationTask && (
        <div className="flex items-center gap-1">
          <FieldTogglePill
            label={TASKS_GRID_WATER_RATE_FIELD_LABEL}
            isActive={showWaterRate}
            hasValue={hasWaterRate}
            onToggle={() => toggle('waterRate', !showWaterRate)}
          />
          {showWaterRate && (
            <NumericInputField
              unit={TASKS_GRID_WATER_RATE_UNIT_LABEL}
              inputClassName="h-7 w-24 rounded bg-white"
              min={0}
              allowEmpty
              value={applicationTask.waterRate}
              placeholder={TASKS_GRID_WATER_RATE_PLACEHOLDER}
              onCommit={(waterRate) => commitTaskField(task, { waterRate })}
            />
          )}
          {showWaterRate && hasWaterRate && (
            <ClearFieldButton
              label={TASKS_GRID_WATER_RATE_FIELD_LABEL}
              onClear={() => commitTaskField(task, { waterRate: null })}
            />
          )}
        </div>
      )}
      {applicationTask && (
        <div className="flex items-center gap-1">
          <FieldTogglePill
            label={TASKS_GRID_TARGET_FIELD_LABEL}
            isActive={showTarget}
            hasValue={hasTarget}
            onToggle={() => toggle('target', !showTarget)}
          />
          {showTarget && (
            <TargetSpeciesPicker
              value={applicationTask.targetSpecies}
              onCommit={(items) =>
                commitTaskField(task, { targetSpecies: items })
              }
            />
          )}
          {showTarget && hasTarget && (
            <ClearFieldButton
              label={TASKS_GRID_TARGET_FIELD_LABEL}
              onClear={() => commitTaskField(task, { targetSpecies: [] })}
            />
          )}
        </div>
      )}
      <FieldTogglePill
        label={TASKS_GRID_NOTES_FIELD_LABEL}
        isActive={showNotes}
        hasValue={hasNotes}
        onToggle={() => toggle('notes', !showNotes)}
      />
    </div>
  );
};

export default TaskInlineDetails;
